import React, { useEffect, useState } from 'react';

import {
  Alert, Skeleton, Stack,
} from '@mui/material';
import _ from 'lodash';

import ContentCard from '../../../../../components/ContentCard';
import ContentGroupsList from '../../../../../components/ContentGroupsList';
import DetailsModal, { Fields, Values } from '../../../../../components/DetailsModal';
import { Course } from '../../../../../services/courses/types';
import {
  useGetLessonTemplatesQuery, useNewLessonTemplateMutation, useNewLessonTemplatesBatchMutation,
} from '../../../../../services/lessons';

enum FormMode {
  Single = 'single',
  Bulk = 'bulk',
}

const FORM_MODE_OPTIONS = [
  { value: FormMode.Single, label: 'Single' },
  { value: FormMode.Bulk, label: 'Bulk' },
];

const INITIAL_LESSON_FORM = {
  idPrefix: '',
  labelPrefix: '',
  numLessons: 1,
  lessonId: '',
  lessonGroup: '',
  label: '',
  lessonType: '',
  isTimesheeted: true,
};

interface LessonTemplatesCardProps {
  course?: Course,
  onLessonClick?: (lessonId: string) => void,
}

const LessonTemplatesCard = ({ course, onLessonClick }: LessonTemplatesCardProps) => {
  const { data: lessonTemplatesData, isLoading } = useGetLessonTemplatesQuery(
    { course: course?.id }, { skip: !course },
  );

  const [newLessonTemplate, {
    isSuccess: isNewLessonTemplateSuccess,
    isLoading: isNewLessonTemplateLoading,
    error: newLessonTemplateError,
    reset: resetNewLessonTemplate,
  }] = useNewLessonTemplateMutation();
  const [newBatchLessonTemplate, {
    isSuccess: isNewBatchLessonTemplateSuccess,
    isLoading: isNewBatchLessonTemplateLoading,
    error: newBatchLessonTemplateError,
    reset: resetNewBatchLessonTemplate,
  }] = useNewLessonTemplatesBatchMutation();

  const [
    newLessonTemplateFormMode,
    setNewLessonTemplateFormMode,
  ] = useState<FormMode>(FormMode.Single);
  const [newLessonTemplateForm, setNewLessonTemplateForm] = useState({});
  const [newLessonTemplateFormFields, setNewLessonTemplateFormFields] = useState<Fields>({});
  const [newLessonTemplateFormOpen, setNewLessonTemplateFormOpen] = useState<boolean>(false);
  const [selectedLessonTemplateId, setSelectedLessonTemplateId] = useState<string>();

  const onLessonTemplateCreate = (values?: Values) => {
    if (course?.id) {
      if (newLessonTemplateFormMode === FormMode.Single) {
        newLessonTemplate({
          course: course.id,
          attributes: values,
        });
      } else {
        newBatchLessonTemplate({
          course: course.id,
          idPrefix: values?.idPrefix,
          labelPrefix: values?.labelPrefix,
          numLessons: values?.numLessons,
          attributes: _.omit(values, ['idPrefix', 'labelPrefix', 'numLessons']),
        });
      }
    }
  };

  const refreshFormFields = () => {
    const metadata = lessonTemplatesData?.metadata;
    setNewLessonTemplateFormFields({
      mode: {
        editable: true,
        type: 'select',
        label: 'Mode',
        typeOptions: FORM_MODE_OPTIONS,
      },
      ...(metadata && _.pick(metadata, [
        'lessonType',
        'isTimesheeted',
        'lessonGroup',
        ...(newLessonTemplateFormMode === FormMode.Single ?
          ['lessonId', 'label'] : []),
      ])),
      // These form fields are manually created as they are not
      // part of the lesson template metadata
      ...(newLessonTemplateFormMode === FormMode.Bulk && {
        idPrefix: {
          editable: true,
          type: 'text',
          label: 'Lesson ID Prefix',
        },
        labelPrefix: {
          editable: true,
          type: 'text',
          label: 'Lesson Label Prefix',
        },
        numLessons: {
          editable: true,
          type: 'number',
          label: 'Number of Lessons',
        },
      }),
    });
  };

  const resetFormValues = () => {
    setNewLessonTemplateForm({
      mode: newLessonTemplateFormMode,
      ...INITIAL_LESSON_FORM,
    });
  };

  const resetModal = async () => {
    await resetNewLessonTemplate();
    await resetNewBatchLessonTemplate();
    resetFormValues();
    refreshFormFields();
  };

  const closeModal = async () => {
    setNewLessonTemplateFormOpen(false);
    await resetModal();
  };

  useEffect(() => {
    resetModal();
  }, [newLessonTemplateFormMode]);

  // The new lesson template form uses the metadata, so must be refreshed once metadata is available
  useEffect(() => {
    resetModal();
  }, [lessonTemplatesData?.metadata]);

  // Close modal after stream is created
  useEffect(() => {
    if (isNewLessonTemplateSuccess || isNewBatchLessonTemplateSuccess) {
      closeModal();
    }
  }, [isNewLessonTemplateSuccess, isNewBatchLessonTemplateSuccess]);

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Lesson Templates">
      <Stack spacing={2}>
        <Alert severity="warning">
          Adding lessons for a course does not automatically add
          lessons to streams or classes.
        </Alert>
        <ContentGroupsList
          columns={[
            {
              field: 'lessonId',
              headerName: 'ID',
              width: 80,
            },
            {
              field: 'combinedLabel',
              headerName: 'Label',
              width: 400,
            },
          ]}
          data={lessonTemplatesData?.data || []}
          loading={isLoading}
          actions={{
            create: {
              fn: () => setNewLessonTemplateFormOpen(true),
              label: 'Add Template',
            },
          }}
          onRowClick={({ row }) => {
            setSelectedLessonTemplateId(row.id);
            if (onLessonClick) onLessonClick(row.lessonId);
          }}
          selectedRowId={selectedLessonTemplateId}
        />
        <DetailsModal
          open={newLessonTemplateFormOpen}
          handleClose={closeModal}
          error={newLessonTemplateError || newBatchLessonTemplateError}
          title="Add New Template"
          fields={newLessonTemplateFormFields}
          values={newLessonTemplateForm}
          onFieldChange={(key, newValue) => {
            if (key === 'mode') {
              setNewLessonTemplateFormMode(newValue as FormMode);
            }
          }}
          buttons={[{
            fn: (values) => onLessonTemplateCreate(_.omit(values, 'mode')),
            label: 'Add',
            loading: isNewLessonTemplateLoading || isNewBatchLessonTemplateLoading,
          }]}
        />
      </Stack>
    </ContentCard>
  );
};

export default LessonTemplatesCard;
